"use client";

import {
  LEGACY_SPANISH_PLAN_STORAGE_KEY,
  getSpanishPlanStorageKey,
} from "@/lib/language-journey/spanish-plan-storage";
import { getLanguageJourneyByCode } from "@/lib/languageJourneyClient";
import {
  type SpanishJourneyGoalProfile,
  type SpanishJourneyPlan,
  SpanishJourneyPlanSchema,
  buildFallbackSpanishJourneyPlan,
  validateSpanishJourneyPlan,
} from "@/lib/spanishJourneyPlannerClient";
import { supabase } from "@/lib/supabaseClient";
import { useCallback, useEffect, useState } from "react";
import { useLanguageJourneyCatalog } from "./useLanguageJourneyCatalog";

const LEGACY_STORAGE_KEY = LEGACY_SPANISH_PLAN_STORAGE_KEY;
const getStorageKey = getSpanishPlanStorageKey;

function loadLocalPlan(storageKey: string): SpanishJourneyPlan | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    return validateSpanishJourneyPlan(SpanishJourneyPlanSchema.parse(JSON.parse(raw)));
  } catch {
    window.localStorage.removeItem(storageKey);
    return null;
  }
}

function saveLocalPlan(storageKey: string, plan: SpanishJourneyPlan | null) {
  if (typeof window === "undefined") return;
  if (!plan) {
    window.localStorage.removeItem(storageKey);
    return;
  }
  window.localStorage.setItem(storageKey, JSON.stringify(plan));
}

function loadGuestPlan(): SpanishJourneyPlan | null {
  const guestStorageKey = getStorageKey(null);
  const guestPlan = loadLocalPlan(guestStorageKey);
  if (guestPlan || typeof window === "undefined") return guestPlan;

  const legacyPlan = loadLocalPlan(LEGACY_STORAGE_KEY);
  if (!legacyPlan) return null;

  saveLocalPlan(guestStorageKey, legacyPlan);
  window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  return legacyPlan;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
  } catch {
    return {};
  }
}

export function useSpanishJourneyPlan({
  enabled,
  userId,
}: {
  enabled: boolean;
  userId: string | null;
}) {
  const isAuthenticated = Boolean(userId);
  const storageKey = getStorageKey(userId);
  const [plan, setPlanState] = useState<SpanishJourneyPlan | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { error: catalogError } = useLanguageJourneyCatalog("es-ES", enabled);
  const catalogReady = Boolean(getLanguageJourneyByCode("es-ES"));

  useEffect(() => {
    if (!enabled) {
      setPlanState(null);
      setLoading(false);
      return;
    }
    // Never validate/delete a saved plan against an unloaded course.
    if (!catalogReady) {
      setLoading(!catalogError);
      if (catalogError) setError("Could not load your Spanish Journey. Try again from Journey.");
      return;
    }
    setError(null);

    let cancelled = false;
    if (userId && typeof window !== "undefined") {
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
    const local = userId ? loadLocalPlan(storageKey) : loadGuestPlan();
    setPlanState(local);

    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    void (async () => {
      try {
        const response = await fetch("/api/journey-plans/spanish", {
          headers: await getAuthHeaders(),
          cache: "no-store",
        });
        if (!response.ok) throw new Error("Could not sync your saved Spanish plan.");
        const payload = (await response.json()) as { plan?: unknown };
        let remote = payload.plan
          ? validateSpanishJourneyPlan(SpanishJourneyPlanSchema.parse(payload.plan))
          : null;
        if (
          local &&
          (!remote || new Date(local.updatedAt).getTime() > new Date(remote.updatedAt).getTime())
        ) {
          const saveResponse = await fetch("/api/journey-plans/spanish", {
            method: "PUT",
            headers: { "Content-Type": "application/json", ...(await getAuthHeaders()) },
            body: JSON.stringify(local),
          });
          if (!saveResponse.ok) throw new Error("Could not sync your saved Spanish plan.");
          const savePayload = (await saveResponse.json()) as { plan?: unknown };
          remote = validateSpanishJourneyPlan(SpanishJourneyPlanSchema.parse(savePayload.plan));
        }
        if (!cancelled && remote) {
          setPlanState(remote);
          saveLocalPlan(storageKey, remote);
        }
      } catch (loadError) {
        if (!cancelled && !local) {
          setError(loadError instanceof Error ? loadError.message : "Could not load your plan.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, isAuthenticated, storageKey, userId, catalogReady, catalogError]);

  const generatePlan = useCallback(
    async (profile: SpanishJourneyGoalProfile) => {
      setGenerating(true);
      setError(null);
      try {
        if (!isAuthenticated) {
          return buildFallbackSpanishJourneyPlan(profile);
        }

        const response = await fetch("/api/journey-plans/spanish/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(await getAuthHeaders()) },
          body: JSON.stringify(profile),
        });
        if (!response.ok) throw new Error("Chicky could not build your plan yet.");
        const payload = (await response.json()) as { plan?: unknown };
        const nextPlan = validateSpanishJourneyPlan(SpanishJourneyPlanSchema.parse(payload.plan));
        return nextPlan;
      } catch (generateError) {
        const fallback = buildFallbackSpanishJourneyPlan(profile);
        setError(
          generateError instanceof Error
            ? `${generateError.message} We kept the reviewed course order.`
            : "We kept the reviewed course order."
        );
        return fallback;
      } finally {
        setGenerating(false);
      }
    },
    [isAuthenticated]
  );

  const savePlan = useCallback(
    async (nextPlan: SpanishJourneyPlan) => {
      const validated = validateSpanishJourneyPlan({
        ...nextPlan,
        updatedAt: new Date().toISOString(),
      });
      setSaving(true);
      setError(null);
      setPlanState(validated);
      saveLocalPlan(storageKey, validated);

      if (!isAuthenticated) {
        setSaving(false);
        return validated;
      }

      try {
        const response = await fetch("/api/journey-plans/spanish", {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...(await getAuthHeaders()) },
          body: JSON.stringify(validated),
        });
        if (!response.ok) throw new Error("Your plan is saved on this device but not synced yet.");
        const payload = (await response.json()) as { plan?: unknown };
        const remote = validateSpanishJourneyPlan(SpanishJourneyPlanSchema.parse(payload.plan));
        setPlanState(remote);
        saveLocalPlan(storageKey, remote);
        return remote;
      } catch (saveError) {
        setError(saveError instanceof Error ? saveError.message : "Could not sync your plan.");
        return validated;
      } finally {
        setSaving(false);
      }
    },
    [isAuthenticated, storageKey]
  );

  const clearPlan = useCallback(async () => {
    const previousPlan = plan;
    setPlanState(null);
    saveLocalPlan(storageKey, null);
    setError(null);
    if (!isAuthenticated) return;
    try {
      const response = await fetch("/api/journey-plans/spanish", {
        method: "DELETE",
        headers: await getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Could not remove your saved Spanish plan.");
    } catch {
      setPlanState(previousPlan);
      saveLocalPlan(storageKey, previousPlan);
      setError("We could not remove your saved Spanish plan. Please try again.");
    }
  }, [isAuthenticated, plan, storageKey]);

  return {
    plan,
    loading,
    generating,
    saving,
    error,
    generatePlan,
    savePlan,
    clearPlan,
  };
}
